import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserGarden } from '../hooks/useUserGarden';
import { useGardenerProfile } from '../hooks/useGardenerProfile';
import { supabase } from '../lib/supabase';
import Breadcrumb from '../components/Breadcrumb';
import GardenPlantCard from '../components/GardenPlantCard';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { generateMyGardenBreadcrumb } from '../utils/breadcrumb';
import type { UserPlant } from '../types/garden';
import { Loader2, Plus, Calendar as CalendarIcon, Grid, CheckCircle2, Sprout, ArrowRight, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ArrowUp, Shovel } from 'lucide-react';
import AuthGuard from '../components/auth/AuthGuard';
import { normalizeDate, createDateAtNoon, isFrostSensitive, isColdHardy } from '../utils/dateUtils';

type ViewMode = 'grid' | 'calendar';

interface TimelineEvent {
  sowIndoorsStart: string | null;
  sowIndoorsEnd: string | null;
  transplantStart: string | null;
  transplantEnd: string | null;
  directSowStart: string | null;
  directSowEnd: string | null;
}

interface PlantTimeline {
  id: string;
  name: string;
  imageUrl: string;
  events: TimelineEvent;
  expanded: boolean;
  frostTolerance: string | null;
}

function getDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export default function MyGarden() {
  const navigate = useNavigate();
  const { plants: gardenPlants, loading: plantsLoading, error: plantsError, deletePlant, refetch: refetchGarden, recalculateGardenDates } = useUserGarden();
  const { profile, loading: profileLoading } = useGardenerProfile();
  const { setItems } = useBreadcrumb();
  const [lastFrostDate, setLastFrostDate] = useState<string>('');
  const [firstFrostDate, setFirstFrostDate] = useState<string>('');
  const [savingDates, setSavingDates] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [viewStart, setViewStart] = useState(() => {
    const today = new Date();
    return getMonthStart(today);
  });
  const [expandedPlants, setExpandedPlants] = useState<Record<string, boolean>>({});
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [headerSticky, setHeaderSticky] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const calendarHeaderRef = useRef<HTMLDivElement>(null);
  const daysHeaderRef = useRef<HTMLDivElement>(null);

  const CalendarLegend = () => (
    <div className="flex flex-wrap items-center gap-4 text-sm">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-green-200 border border-green-300 rounded"></div>
        <span>Indoor Sowing</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-blue-200 border border-blue-300 rounded"></div>
        <span>Transplanting</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-amber-200 border border-amber-300 rounded"></div>
        <span>Direct Sowing</span>
      </div>
    </div>
  );

  React.useEffect(() => {
    setItems(generateMyGardenBreadcrumb());
  }, [setItems]);

  React.useEffect(() => {
    if (profile) {
      if (profile.last_frost_date) {
        setLastFrostDate(profile.last_frost_date.split('T')[0]);
      }
      if (profile.first_frost_date) {
        setFirstFrostDate(profile.first_frost_date.split('T')[0]);
      }
    }
  }, [profile]);

  React.useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  // Initialize expanded state for all plants
  useEffect(() => {
    if (!plantsLoading && gardenPlants.length > 0) {
      const initialExpandedState: Record<string, boolean> = {};
      gardenPlants.forEach(plant => {
        initialExpandedState[plant.id] = true; // Default to expanded
      });
      setExpandedPlants(initialExpandedState);
    }
  }, [plantsLoading, gardenPlants]);

  // Handle scroll to show/hide scroll-to-top button and sticky header
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
      
      // Make calendar header sticky when scrolling
      if (calendarHeaderRef.current && daysHeaderRef.current && viewMode === 'calendar') {
        const headerRect = calendarHeaderRef.current.getBoundingClientRect();
        
        if (headerRect.top <= 0) {
          setHeaderSticky(true);
        } else {
          setHeaderSticky(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [viewMode]);

  const handleSaveFrostDates = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) return;

    setSavingDates(true);
    setDateError(null);

    try {
      const { error } = await supabase
        .from('gardener_profiles')
        .update({
          first_frost_date: firstFrostDate || null,
          last_frost_date: lastFrostDate || null
        })
        .eq('id', profile.id);

      if (error) throw error;
      
      // Show success message
      setSaveSuccess(true);
      
      // Recalculate garden dates
      await recalculateGardenDates();
      
    } catch (err) {
      console.error('Error saving frost dates:', err);
      setDateError('Failed to save frost dates. Please try again.');
    } finally {
      setSavingDates(false);
    }
  };

  const handleRefreshGarden = async () => {
    setRefreshing(true);
    try {
      await refetchGarden();
    } catch (error) {
      console.error("Failed to refresh garden data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePreviousMonth = () => {
    setViewStart(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return getMonthStart(newDate);
    });
  };

  const handleNextMonth = () => {
    setViewStart(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return getMonthStart(newDate);
    });
  };

  const togglePlantExpanded = (plantId: string) => {
    setExpandedPlants(prev => ({
      ...prev,
      [plantId]: !prev[plantId]
    }));
  };

  const toggleAllPlants = (expanded: boolean) => {
    const newState: Record<string, boolean> = {};
    Object.keys(expandedPlants).forEach(id => {
      newState[id] = expanded;
    });
    setExpandedPlants(newState);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleDeletePlant = async (plantId: string) => {
    try {
      await deletePlant(plantId);
    } catch (error) {
      console.error("Failed to delete plant:", error);
    }
  };

  const plantTimelines: PlantTimeline[] = gardenPlants.map(plant => ({
    id: plant.id,
    name: plant.plant.name,
    imageUrl: plant.plant.image_url,
    frostTolerance: plant.plant.frost_tolerance,
    events: {
      sowIndoorsStart: plant.sow_indoors_start_date,
      sowIndoorsEnd: plant.sow_indoors_end_date,
      transplantStart: plant.transplant_start_date,
      transplantEnd: plant.transplant_end_date,
      directSowStart: plant.direct_sow_start_date,
      directSowEnd: plant.direct_sow_end_date
    },
    expanded: expandedPlants[plant.id] || false
  }));

  const sortedTimelines = [...plantTimelines].sort((a, b) => 
    a.name.localeCompare(b.name)
  );

  const timelineEnd = getMonthEnd(viewStart);
  const timelineDuration = timelineEnd.getTime() - viewStart.getTime();

  const daysInMonth = getDaysInMonth(viewStart);
  const dateHeaders = Array.from({ length: daysInMonth }, (_, i) => {
    const date = new Date(viewStart);
    date.setDate(i + 1);
    return date;
  });

  const getEventStyle = (date: string | null, endDate: string | null) => {
    if (!date || !endDate) return null;
    
    // Normalize dates to prevent timezone issues
    const normalizedStart = normalizeDate(date);
    const normalizedEnd = normalizeDate(endDate);
    
    if (!normalizedStart || !normalizedEnd) return null;
    
    // Create Date objects from normalized dates (at noon to avoid any timezone edge cases)
    const startParts = normalizedStart.split('-').map(Number);
    const endParts = normalizedEnd.split('-').map(Number);
    
    const start = new Date(startParts[0], startParts[1] - 1, startParts[2], 12, 0, 0);
    const end = new Date(endParts[0], endParts[1] - 1, endParts[2], 12, 0, 0);
    
    if (end < viewStart || start > timelineEnd) return null;
    
    const effectiveStart = start < viewStart ? viewStart : start;
    const effectiveEnd = end > timelineEnd ? timelineEnd : end;
    
    const left = ((effectiveStart.getTime() - viewStart.getTime()) / timelineDuration) * 100;
    const width = ((effectiveEnd.getTime() - effectiveStart.getTime()) / timelineDuration) * 100;

    return {
      left: `${left}%`,
      width: `${width}%`
    };
  };

  if (plantsError) {
    return (
      <AuthGuard>
        <div className="container mx-auto px-4">
          <Breadcrumb />
          <div className="text-red-600">{plantsError}</div>
        </div>
      </AuthGuard>
    );
  }

  const loading = plantsLoading || profileLoading;

  return (
    <AuthGuard>
      <div className="container mx-auto px-4">
        <Breadcrumb />
        
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">My Garden</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white rounded-lg border p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${
                  viewMode === 'grid' 
                    ? 'bg-primary-500 text-white' 
                    : 'text-gray-600 hover:text-primary-600'
                }`}
                title="Grid view"
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 rounded ${
                  viewMode === 'calendar' 
                    ? 'bg-primary-500 text-white' 
                    : 'text-gray-600 hover:text-primary-600'
                }`}
                title="Calendar view"
              >
                <CalendarIcon className="h-5 w-5" />
              </button>
            </div>
            <button
              onClick={() => navigate('/plants')}
              className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add Plants
            </button>
          </div>
        </div>

        <form onSubmit={handleSaveFrostDates} className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <CalendarIcon className="h-5 w-5 text-primary-500" />
            <h2 className="font-semibold">Growing Season</h2>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Spring Frost
                </label>
                <input
                  type="date"
                  value={lastFrostDate}
                  onChange={(e) => setLastFrostDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Fall Frost
                </label>
                <input
                  type="date"
                  value={firstFrostDate}
                  onChange={(e) => setFirstFrostDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="min-h-[2rem]">
                {dateError && (
                  <p className="text-sm text-red-600">{dateError}</p>
                )}
                {saveSuccess && (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <p className="text-sm">Dates saved!</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleRefreshGarden}
                  disabled={refreshing}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-primary-500 text-primary-500 rounded-md hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sprout className="h-4 w-4" />
                  )}
                  <span>Refresh Garden</span>
                </button>
                <button
                  type="submit"
                  disabled={savingDates}
                  className="flex items-center justify-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingDates ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Save Frost Dates'
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="text-gray-600">Loading your garden...</div>
          </div>
        ) : gardenPlants.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">Your garden is empty. Start by adding some plants!</p>
            <button
              onClick={() => navigate('/plants')}
              className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors mx-auto"
            >
              <Plus className="h-5 w-5" />
              Browse Plants
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {gardenPlants.map((userPlant) => (
              <GardenPlantCard
                key={userPlant.id}
                userPlant={userPlant}
                onEdit={() => {}}
                onDelete={handleDeletePlant}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div ref={calendarHeaderRef} className={`flex items-center justify-between mb-6 ${headerSticky ? 'sticky top-0 z-20 bg-white py-4 shadow-md' : ''}`}>
              <h3 className="text-lg font-semibold">
                {viewStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleAllPlants(true)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 px-2 py-1 border border-primary-200 rounded-md hover:bg-primary-50"
                  >
                    <ChevronDown className="h-3 w-3" />
                    <span>Expand All</span>
                  </button>
                  <button
                    onClick={() => toggleAllPlants(false)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 px-2 py-1 border border-primary-200 rounded-md hover:bg-primary-50"
                  >
                    <ChevronUp className="h-3 w-3" />
                    <span>Collapse All</span>
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousMonth}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <CalendarLegend />
            </div>

            <div 
              ref={daysHeaderRef} 
              className={`flex mb-4 ${headerSticky ? 'sticky top-16 z-10 bg-white py-2' : ''}`}
            >
              <div className="w-48 flex-shrink-0"></div>
              <div className="flex-1 flex">
                {dateHeaders.map((date, i) => (
                  <div 
                    key={i}
                    className={`flex-1 text-center text-xs font-medium text-gray-600 border-l ${
                      date.getDay() === 0 ? 'border-l-2 border-l-gray-300' : ''
                    }`}
                  >
                    {date.getDate()}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 relative">
              {sortedTimelines.map((timeline) => (
                <div key={timeline.id} className="space-y-1">
                  {/* Plant Header with Toggle */}
                  <div 
                    className="flex items-center gap-2 mb-1 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    onClick={() => togglePlantExpanded(timeline.id)}
                  >
                    <button className="text-gray-500 hover:text-primary-600">
                      {expandedPlants[timeline.id] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                     <img 
                       src={timeline.imageUrl} 
                       alt={timeline.name}
                       className="w-6 h-6 rounded object-cover"
                     />
                     <div className="min-w-0">
                       <p className="font-medium truncate text-sm">{timeline.name}</p>
                     </div>
                  </div>

                  {/* Timeline Rows - Only show if expanded */}
                  {expandedPlants[timeline.id] && (
                    <>
                      {/* Indoor Sowing Row */}
                      {timeline.events.sowIndoorsStart && timeline.events.sowIndoorsEnd && (
                        <div className="flex h-6">
                          <div className="w-48 flex-shrink-0 pr-4">
                            <span className="text-xs text-gray-500">Indoor Sowing</span>
                          </div>
                          <div className="flex-1 relative border-l border-r">
                            <div 
                              className="absolute inset-y-0 bg-green-200 border border-green-300 rounded"
                              style={getEventStyle(timeline.events.sowIndoorsStart, timeline.events.sowIndoorsEnd)}
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Sprout className="h-3 w-3 text-green-700" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Direct Sowing Row */}
                      {timeline.events.directSowStart && timeline.events.directSowEnd && (
                        <div className="flex h-6">
                          <div className="w-48 flex-shrink-0 pr-4">
                            <span className="text-xs text-gray-500">Direct Sowing</span>
                          </div>
                          <div className="flex-1 relative border-l border-r">
                            <div 
                              className="absolute inset-y-0 bg-amber-200 border border-amber-300 rounded"
                              style={getEventStyle(timeline.events.directSowStart, timeline.events.directSowEnd)}
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Shovel className="h-3 w-3 text-amber-700" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Transplanting Row */}
                      {timeline.events.transplantStart && timeline.events.transplantEnd && (
                        <div className="flex h-6">
                          <div className="w-48 flex-shrink-0 pr-4">
                            <span className="text-xs text-gray-500">Transplanting</span>
                          </div>
                          <div className="flex-1 relative border-l border-r">
                            <div 
                              className="absolute inset-y-0 bg-blue-200 border border-blue-300 rounded"
                              style={getEventStyle(timeline.events.transplantStart, timeline.events.transplantEnd)}
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <ArrowRight className="h-3 w-3 text-blue-700" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <CalendarLegend />
            </div>
          </div>
        )}

        {/* Scroll to top button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 bg-primary-500 text-white p-3 rounded-full shadow-lg hover:bg-primary-600 transition-colors z-10"
            aria-label="Scroll to top"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        )}
      </div>
    </AuthGuard>
  );
}